import { literals } from "@/constants/literals";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { processImageDeletionRequest } from "@/lib/services/images";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import { useUserStore } from "@/store/useUser";
import { formatDateDDMmmYYYY } from "@/lib/dates";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { formatBooleanText, formatOptionalText } from "@/lib/texts.utils";
import type { RequestPhotoInfo } from "@/types/types.d";
import { RequestModalDetail } from "./RequestModalDetail";

export type RequestDeleteModalContentProps = {
  photoURL: string;
  image: RequestPhotoInfo;
  loadRequests: () => Promise<void>;
  closeModal: () => void;
  deselectPhoto: () => void;
};

const RequestDeleteModalContent = ({
  photoURL,
  image,
  loadRequests,
  closeModal,
  deselectPhoto,
}: RequestDeleteModalContentProps) => {
  const { upLoader, downLoader } = useLoader();
  const { isSupervisor } = useUserStore();

  const answerPetition = async (status: "APPROVED" | "REJECTED") => {
    if (!isSupervisor) {
      appToast.error(literals.aProblem, literals.notAllowed);
      return;
    }

    if (!image) return;

    try {
      upLoader();

      await processImageDeletionRequest(image.requestId, status);

      if (status === "APPROVED") {
        appToast.success(
          literals.completedProcess,
          `${literals.deletionPhoto}${image.fileName}`,
        );
      } else {
        appToast.success(
          literals.completedProcess,
          `${literals.deletionRejected}${image.fileName}`,
        );
      }
      await loadRequests();
      closeModal();
      deselectPhoto();
    } catch {
      appToast.error(literals.aProblem, literals.requestError);
    } finally {
      downLoader();
    }
  };

  return (
    <>
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="w-full flex flex-row justify-start items-center gap-x-4">
          <div className="w-1/2">
            {photoURL && (
              <img
                src={photoURL}
                alt={image.fileName}
                className="w-full rounded"
              />
            )}
          </div>
          <RequestModalDetail
            label={literals.requestedBy}
            value={formatOptionalText(image.requestedBy)}
          />
        </div>
        <div className="pb-3 space-y-3 mt-3">
          <div className="grid grid-cols-3 gap-2">
            <RequestModalDetail
              label={literals.date}
              value={formatDateDDMmmYYYY(image.createdAt)}
            />
            <RequestModalDetail
              label={literals.workstation}
              value={image.area}
            />
            <RequestModalDetail
              label={literals.visibleClient}
              value={formatBooleanText(image.imageGroupVisible)}
            />
          </div>

          <div>
            <RequestModalDetail
              label={literals.description}
              value={formatOptionalText(image.imageGroupDescription)}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <RequestModalDetail label={literals.step} value={image.step} />
            <RequestModalDetail
              label={literals.customerOrderNoServ}
              value={formatOptionalText(image.customerOrderNoserv)}
            />
            <RequestModalDetail
              label={literals.orderIn}
              value={formatOptionalText(image.orderIn)}
            />
            <RequestModalDetail
              label={literals.serialIn}
              value={formatOptionalText(image.serialIn)}
            />
            <RequestModalDetail
              label={literals.workOrder}
              value={formatOptionalText(image.sapWorkOrder)}
            />
            <RequestModalDetail
              label={literals.orderOut}
              value={formatOptionalText(image.orderOut)}
            />
            <RequestModalDetail
              label={literals.serialOut}
              value={formatOptionalText(image.serialOut)}
            />
            <RequestModalDetail
              label={literals.document9R1}
              value={formatOptionalText(image.document9r1)}
            />
            <RequestModalDetail
              label={literals.returnToOrigin}
              value={formatBooleanText(image.originReturn)}
            />
            <RequestModalDetail
              label={literals.client}
              value={formatOptionalText(image.customer)}
            />
            <RequestModalDetail
              label={literals.ownPackaging}
              value={formatBooleanText(image.ownPackaging)}
            />
            <RequestModalDetail
              label={literals.correctPackaging}
              value={formatBooleanText(image.correctPackaging)}
            />
            <RequestModalDetail
              label={literals.damagedPackaging}
              value={formatBooleanText(image.damagedPackaging)}
            />
            <RequestModalDetail
              label={literals.packagingReference}
              value={formatOptionalText(image.packagingReference)}
            />
            <RequestModalDetail
              label={literals.ownPackagingOut}
              value={formatBooleanText(image.ownPackagingOut)}
            />
            <RequestModalDetail
              label={literals.iberiaPackagingOut}
              value={formatBooleanText(image.iberiaPackagingOut)}
            />
          </div>
        </div>
      </div>
      <div className="w-full border-t border-t-ib-gray-borders flex flex-row justify-between items-center pt-4">
        <div className="text-ib-pt-lg text-ib-gray-main">
          {literals.wishDeletePhoto}
        </div>
        <div className="flex flex-row gap-x-4 justify-start">
          <div className="w-40">
            <SecondaryButton
              size="medium"
              colour="red"
              label={literals.rejectPetition}
              buttonType="button"
              onClick={() => answerPetition("REJECTED")}
            />
          </div>
          <div className="w-40">
            <PrimaryButton
              size="medium"
              colour="red"
              label={literals.acceptPetition}
              type="button"
              onClick={() => answerPetition("APPROVED")}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default RequestDeleteModalContent;
